import {
  CompanyAccessSummary,
  CompanyDetailsResponse,
  CompanySeatSummary,
  ErrorResponse,
  Timestamp,
} from '@handover/shared-types';

const BILLING_LOCKED_STATUSES = new Set(['inactive', 'unpaid', 'canceled']);
const ADMIN_ROLES = new Set(['admin', 'superAdmin']);
const LOCKED_ADMIN_PATHS = ['/billing'];

export const getCompanyAccessSummaryFromQuery = (
  companyDetails?: CompanyDetailsResponse,
  error?: unknown
): CompanyAccessSummary | null => {
  if (companyDetails?.accessSummary) {
    return companyDetails.accessSummary;
  }

  const responseError = error as ErrorResponse | undefined;
  return responseError?.data?.companyAccess || null;
};

export const getSeatSummaryFromQuery = (
  companyDetails?: CompanyDetailsResponse
): CompanySeatSummary | null => companyDetails?.seatSummary || null;

export const isAdminRole = (role?: string | null): boolean =>
  typeof role === 'string' && ADMIN_ROLES.has(role);

export const isBillingLockedStatus = (
  accessSummary?: CompanyAccessSummary | null
): boolean =>
  Boolean(accessSummary && BILLING_LOCKED_STATUSES.has(accessSummary.billingStatus));

export const canAccessLockedAdminRoute = (
  pathname: string,
  role?: string | null
): boolean => {
  if (!isAdminRole(role)) {
    return false;
  }

  return LOCKED_ADMIN_PATHS.some(
    path => pathname === path || pathname.startsWith(`${path}/`)
  );
};

export const formatBillingStatusLabel = (status?: string | null): string => {
  if (!status) {
    return 'Not configured';
  }

  return status
    .split('_')
    .map(value => value.charAt(0).toUpperCase() + value.slice(1))
    .join(' ');
};

export const formatWorkspaceDate = (
  value?: string | Timestamp | null
): string | null => {
  if (!value) {
    return null;
  }

  let date: Date | null = null;

  if (typeof value === 'string') {
    date = new Date(value);
  } else if (
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as { toDate?: () => Date }).toDate === 'function'
  ) {
    date = (value as { toDate: () => Date }).toDate();
  } else if (
    typeof value === 'object' &&
    'seconds' in value &&
    typeof (value as { seconds?: unknown }).seconds === 'number'
  ) {
    date = new Date((value as { seconds: number }).seconds * 1000);
  } else if (
    typeof value === 'object' &&
    '_seconds' in (value as Record<string, unknown>) &&
    typeof (value as { _seconds?: unknown })._seconds === 'number'
  ) {
    const legacyTimestamp = value as unknown as { _seconds: number };
    date = new Date(legacyTimestamp._seconds * 1000);
  }

  if (!date) {
    return null;
  }

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString();
};

export const getWorkspaceStatusMessage = (
  accessSummary?: CompanyAccessSummary | null
): string | null => {
  if (!accessSummary) {
    return null;
  }

  switch (accessSummary.billingStatus) {
    case 'past_due':
      return accessSummary.graceEndsAt
        ? `Payment did not go through. Your workspace is still active until ${formatWorkspaceDate(accessSummary.graceEndsAt)}. Update billing before then to avoid interruption.`
        : 'Payment did not go through. Update billing to keep the workspace active.';
    case 'unpaid':
      return 'This workspace is temporarily locked because payment could not be collected. Recover billing to restore full access.';
    case 'canceled':
      return 'This workspace is inactive because the subscription was canceled. Reopen billing if you need to reactivate it.';
    case 'inactive':
      return 'This workspace is not active yet. Complete billing setup to start using it.';
    default:
      return null;
  }
};
